/*
  nc-menu-nav.js — el menú te lleva a la sección y se ve el viaje (9-oct).
  Antes, al tocar «How it works» en el menú del móvil la página saltaba DETRÁS del menú, que seguía abierto,
  y no se veía a dónde te había llevado. Ahora, si el enlace apunta a una sección de ESTA página:
    1. el enlace tocado se rellena y los demás se apagan (confirma el toque);
    2. el menú se cierra solo, deslizándose;
    3. a la vez la página se desliza hasta la sección (se ve el recorrido, no un salto);
    4. al llegar, el antetítulo y el título de la sección se escriben de izquierda a derecha.
  Los enlaces a otras páginas (Contact) siguen igual. Con «reducir movimiento» va directo y sin animaciones.
  Si el cliente toca la pantalla durante el deslizamiento, se para ahí: nunca le peleamos el scroll.
*/
(() => {
  if (window.ncMenuNav) return;
  window.ncMenuNav = true;

  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  let glide = null;

  const stopGlide = () => {
    if (!glide) return;
    const { raf, done } = glide;
    glide = null;
    cancelAnimationFrame(raf);
    done();
  };
  ['touchstart', 'wheel', 'keydown'].forEach((type) => window.addEventListener(type, stopGlide, { passive: true }));

  // getY se recalcula en cada frame: si algo de encima cambia de alto (imágenes que cargan), el destino se corrige solo.
  // done se llama una vez, ya en la frenada final (80 %), para que el título empiece a escribirse mientras la página se asienta.
  const glideTo = (getY, onArrive) => {
    stopGlide();
    let arrived = false;
    const done = () => {
      if (arrived) return;
      arrived = true;
      onArrive();
    };
    const from = window.scrollY;
    const distance = Math.abs(getY() - from);
    if (reduced() || distance < 2) {
      window.scrollTo(0, getY());
      done();
      return;
    }
    const duration = Math.min(1400, Math.max(900, 500 + distance * 0.25));
    const g = (glide = { raf: 0, done });
    let start = null;
    const step = (now) => {
      if (glide !== g) return;
      if (start === null) start = now;
      const p = Math.min(1, (now - start) / duration);
      window.scrollTo(0, from + (getY() - from) * easeInOut(p));
      if (p >= 0.8) done();
      if (p < 1) {
        g.raf = requestAnimationFrame(step);
      } else {
        glide = null;
        done();
      }
    };
    g.raf = requestAnimationFrame(step);
  };

  const headerHeight = () => document.querySelector('.section-header')?.offsetHeight || 0;

  const targetY = (el, goingUp) => {
    let y = el.getBoundingClientRect().top + window.scrollY;
    // Subiendo, la cabecera fija reaparece y taparía el principio de la sección. En la animación no hace falta:
    // mientras está fijada esconde la cabecera.
    if (goingUp && !el.closest('nc-scroll-scrub')) y -= headerHeight();
    const max = document.documentElement.scrollHeight - window.innerHeight;
    return Math.max(0, Math.min(Math.ceil(y), max));
  };

  // Antetítulo y título de la sección de destino que aún no se ven: se esconden durante el viaje y se escriben al llegar.
  const arrivalLines = (el) => {
    const section = el.closest('.shopify-section');
    if (!section || reduced()) return [];
    return [...section.querySelectorAll('.nc-eyebrow, .caption-with-letter-spacing, h2')]
      .filter((line) => !line.classList.contains('visually-hidden') && line.textContent.trim())
      .filter((line) => {
        const r = line.getBoundingClientRect();
        return r.height && (r.bottom < 0 || r.top > window.innerHeight);
      })
      .slice(0, 2);
  };

  const reveal = (lines) => {
    if (!lines.length) return;
    requestAnimationFrame(() => requestAnimationFrame(() => lines.forEach((line) => line.classList.add('is-in'))));
    setTimeout(() => {
      lines.forEach((line) => {
        line.classList.remove('nc-arrive', 'is-in');
        line.style.transitionDelay = '';
      });
    }, 1600);
  };

  const travel = (target) => {
    const goingUp = target ? target.getBoundingClientRect().top < 0 : true;
    const lines = target ? arrivalLines(target) : [];
    lines.forEach((line, i) => {
      line.style.transitionDelay = `${i * 0.18}s`;
      line.classList.add('nc-arrive');
    });
    glideTo(() => (target ? targetY(target, goingUp) : 0), () => reveal(lines));
  };

  const closeDrawer = (drawer, event) => {
    const details = drawer.querySelector('details');
    const summary = details && details.querySelector('summary');
    if (!summary || !details.hasAttribute('open')) return;
    drawer.closeMenuDrawer(event, summary);
    summary.setAttribute('aria-expanded', 'false');
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('header-drawer a.menu-drawer__menu-item[href], .header__inline-menu a[href]');
    if (!link) return;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    const id = url.hash ? decodeURIComponent(url.hash.slice(1)) : '';
    const target = id ? document.getElementById(id) : null;
    const toTop = !id && url.pathname === window.location.pathname && !url.search;
    if (!target && !toTop) return; // otra página: navegación normal
    event.preventDefault();

    const drawer = link.closest('header-drawer');
    const details = drawer && drawer.querySelector('details');
    const go = () => {
      if (drawer) closeDrawer(drawer, event);
      history.replaceState(history.state, '', id ? `#${id}` : window.location.pathname + window.location.search);
      travel(target);
    };

    if (!drawer || reduced()) {
      go();
      return;
    }
    link.classList.add('nc-chosen');
    details.classList.add('nc-leaving');
    setTimeout(go, 170);
    setTimeout(() => {
      link.classList.remove('nc-chosen');
      details.classList.remove('nc-leaving');
    }, 700);
  });
})();
